import { getMarkRange, type Editor } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import { LINK_TYPE_FILE } from "../../../../_default/utilities/customContentTags";
import {
  getPendingFileLinkSelection,
  type PendingFileLinkSelection,
} from "./fileLinkSelectionStore";

export type LinkRange = { from: number; to: number };

export function normalizeFileLinkHref(rawHref: string): string {
  const href = (rawHref || "").trim();
  if (!href) {
    return "";
  }

  if (
    href.startsWith("/") ||
    href.startsWith("./") ||
    href.startsWith("../") ||
    href.startsWith("http://") ||
    href.startsWith("https://")
  ) {
    return href;
  }

  return `/${href}`;
}

export function captureLinkRangeFromEditor(editor: Editor): LinkRange | null {
  const { empty, from, to, $from } = editor.state.selection;

  if (!empty && from !== to) {
    return { from, to };
  }

  const linkMark = editor.state.schema.marks.link;
  if (!linkMark) {
    return null;
  }

  const markRange = getMarkRange($from, linkMark);
  if (markRange) {
    return { from: markRange.from, to: markRange.to };
  }

  return null;
}

function findTextRangeInDocument(
  doc: ProseMirrorNode,
  searchText: string
): LinkRange | null {
  const needle = searchText.trim();
  if (!needle) {
    return null;
  }

  let found: LinkRange | null = null;

  doc.descendants((node, pos) => {
    if (found || !node.isText || !node.text) {
      return;
    }

    const index = node.text.indexOf(needle);
    if (index >= 0) {
      found = {
        from: pos + index,
        to: pos + index + needle.length,
      };
    }
  });

  return found;
}

function resolvePendingSelection(
  editor: Editor,
  savedRange: LinkRange | null
): PendingFileLinkSelection | null {
  if (savedRange && savedRange.from !== savedRange.to) {
    return {
      ...savedRange,
      text: editor.state.doc.textBetween(savedRange.from, savedRange.to, " "),
    };
  }

  const pending = getPendingFileLinkSelection();
  if (pending) {
    const { from, to, text } = pending;
    const docSize = editor.state.doc.content.size;

    if (
      from >= 0 &&
      to <= docSize &&
      from < to &&
      editor.state.doc.textBetween(from, to, " ") === text
    ) {
      return pending;
    }

    const located = findTextRangeInDocument(editor.state.doc, text);
    if (located) {
      return { ...located, text };
    }
  }

  const currentRange = captureLinkRangeFromEditor(editor);
  if (currentRange) {
    return {
      ...currentRange,
      text: editor.state.doc.textBetween(currentRange.from, currentRange.to, " "),
    };
  }

  return null;
}

export function buildFileLinkAttributes(href: string) {
  const normalizedHref = normalizeFileLinkHref(href);
  if (!normalizedHref) {
    return null;
  }

  return {
    href: normalizedHref,
    target: "_blank",
    rel: "noopener noreferrer",
    dataLinkType: LINK_TYPE_FILE,
  };
}

export function applyFileLinkAtRange(
  editor: Editor,
  href: string,
  savedRange: LinkRange | null
): boolean {
  const attrs = buildFileLinkAttributes(href);
  if (!attrs) {
    return false;
  }

  const pending = resolvePendingSelection(editor, savedRange);
  if (!pending || pending.from === pending.to) {
    return false;
  }

  const { state, view } = editor;
  const linkMark = state.schema.marks.link;
  if (!linkMark) {
    return false;
  }

  const from = Math.max(0, Math.min(pending.from, state.doc.content.size));
  const to = Math.max(from, Math.min(pending.to, state.doc.content.size));

  if (from === to) {
    return false;
  }

  let tr = state.tr;
  tr = tr.removeMark(from, to, linkMark);
  tr = tr.addMark(from, to, linkMark.create(attrs));

  const italic = state.schema.marks.italic;
  const underline = state.schema.marks.underline;
  if (italic) {
    tr = tr.addMark(from, to, italic.create());
  }
  if (underline) {
    tr = tr.addMark(from, to, underline.create());
  }

  tr = tr.setSelection(TextSelection.create(tr.doc, to));
  view.dispatch(tr);

  return true;
}
